import { CAMERA_OPTIONS } from 'enums/streaming';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { CameraSource } from 'lib_ts/enums/machine-msg.enum';
import { HardwareVersion } from 'lib_ts/enums/machine.enums';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { ICameraStreamResponseMsg } from 'lib_ts/interfaces/machine-msg/i-camera-stream';
import { first } from 'lodash';
import {
  createContext,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { StreamingService } from 'services/streaming.service';

const CONTEXT_NAME = 'StreamingContext';
const DEFAULT_REFRESH_RATE = 5;
const STREAM_REATTEMPT_DELAY_MS = 1000;
const STREAM_MAX_REATTEMPTS = 5;

export interface IStreamingContext {
  sourceOptions: IOption[]; // ? specific sources are only available on specific hw versions

  source: CameraSource;
  readonly setSource: (value: CameraSource) => void;

  paused: boolean;
  readonly togglePaused: () => void;

  lastFrame: Partial<ICameraStreamResponseMsg> | undefined;

  refreshRate: number;
  readonly setRefreshRate: (value: number) => void;

  streamFailed: boolean;
  readonly retryStream: () => void;
}

const DEFAULT: IStreamingContext = {
  sourceOptions: [],

  source: CameraSource.BI_R,
  setSource: () => console.error(`${CONTEXT_NAME}: not init`),

  paused: false,
  togglePaused: () => console.error(`${CONTEXT_NAME}: not init`),

  lastFrame: undefined,

  refreshRate: DEFAULT_REFRESH_RATE,
  setRefreshRate: () => console.error(`${CONTEXT_NAME}: not init`),

  streamFailed: false,
  retryStream: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const StreamingContext = createContext(DEFAULT);

interface IProps {
  machineID: string;
  hwVersion: HardwareVersion;
  defaultSource?: CameraSource;
  children: ReactNode;
}

export const StreamingProvider: FC<IProps> = (props) => {
  const sourceOptions = useMemo(() => {
    switch (props.hwVersion) {
      case HardwareVersion.Arc2022:
      case HardwareVersion.Arc2023:
      case HardwareVersion.Arc2024:
      case HardwareVersion.Arc2025: {
        // ? old hw versions don't support spin detector
        return CAMERA_OPTIONS.filter(
          (m) => m.value !== CameraSource.SPIN_DETECTOR
        );
      }

      case HardwareVersion.Arc2026:
      case HardwareVersion.Arc2027:
      default: {
        return CAMERA_OPTIONS;
      }
    }
  }, [props.hwVersion]);

  const [source, setSource] = useState(() => {
    if (props.defaultSource) {
      // if specified by parent component, only if it exists
      const foundOption = sourceOptions.find(
        (m) => m.value === props.defaultSource
      );

      if (!foundOption) {
        console.error(
          `Streaming provider failed to find ${props.defaultSource} source in source options for ${props.hwVersion}`
        );
      }

      if (foundOption) {
        return foundOption.value as CameraSource;
      }
    }

    // first available to this hw version
    const firstOption = first(sourceOptions);

    if (firstOption) {
      return firstOption.value as CameraSource;
    }

    // fallback
    return CameraSource.BI_DEBUG;
  });

  const [paused, setPaused] = useState(DEFAULT.paused);
  const [refreshRate, setRefreshRate] = useState(DEFAULT.refreshRate);
  const [lastFrame, setLastFrame] = useState(DEFAULT.lastFrame);
  const [retryKey, setRetryKey] = useState(0);
  const [streamFailed, setStreamFailed] = useState(false);

  const streamCancelledRef = useRef(false);

  // handle requesting the latest frame based on the selected source
  useEffect(() => {
    if (paused) {
      return;
    }

    streamCancelledRef.current = false;

    const frameDelayMs = Math.round(1000 / refreshRate);

    const timeout = setTimeout(async () => {
      let attempt = 0;
      const maxAttempts = STREAM_MAX_REATTEMPTS;

      // try grabbing another frame after short delay if stream isn't ready yet
      const reattempt = async () => {
        attempt++;
        await MiscHelper.sleep(STREAM_REATTEMPT_DELAY_MS);
      };

      while (attempt < maxAttempts) {
        try {
          // prevents continued reattempts after unmount
          if (streamCancelledRef.current) {
            return;
          }

          const frame = await StreamingService.getInstance().getLastFrame({
            machineID: props.machineID,
            source,
          });

          if (frame) {
            setLastFrame(frame);
            return;
          }

          await reattempt();
        } catch (err) {
          await reattempt();
        }
      }

      setLastFrame(undefined);
      setStreamFailed(true);
    }, frameDelayMs);

    return () => {
      // prevent continued reattempts after unmount
      clearTimeout(timeout);
      streamCancelledRef.current = true;
    };
  }, [paused, lastFrame, source, refreshRate, retryKey]);

  const state: IStreamingContext = {
    sourceOptions: sourceOptions,

    source: source,
    setSource: setSource,

    paused: paused,
    togglePaused: () => setPaused((prev) => !prev),

    lastFrame: lastFrame,

    refreshRate,
    setRefreshRate: setRefreshRate,

    streamFailed,
    retryStream: () => {
      setStreamFailed(false);
      setRetryKey((prev) => prev + 1);
    },
  };

  return (
    <StreamingContext.Provider value={state}>
      {props.children}
    </StreamingContext.Provider>
  );
};
